// src/utils/undertaking.js
// ─────────────────────────────────────────────────────────────────────────────
// Present the "Declaration" section as an "Undertaking" across every form.
//
// The declaration heading and its opening statement ("I/We declare that:") come
// from backend form config, which we don't control. `toUndertaking` is a
// display-only text transform that rewrites the declaration word-family to the
// undertaking word-family while preserving the original casing:
//
//   "Declaration"            → "Undertaking"
//   "I/We declare that:"     → "I/We undertake that:"
//   "I/We further declare …" → "I/We further undertake …"
//
// It is deliberately conservative: it only touches whole words (so "declarant"
// in a field label is left intact) and never mutates the underlying data — call
// it at the moment text is rendered.
// ─────────────────────────────────────────────────────────────────────────────

// Longer / inflected forms first; every entry is whole-word anchored so a token
// maps to exactly one replacement and unrelated words are never partially hit.
const WORD_MAP = [
  [/\bdeclarations\b/gi, 'undertakings'],
  [/\bdeclaration\b/gi,  'undertaking'],
  [/\bdeclaring\b/gi,    'undertaking'],
  [/\bdeclares\b/gi,     'undertakes'],
  [/\bdeclared\b/gi,     'undertaken'],
  [/\bdeclare\b/gi,      'undertake'],
];

// Mirror the source token's case onto the replacement: ALL CAPS → all caps,
// Capitalised → capitalised, everything else → lower case.
const matchCase = (src, repl) => {
  if (src === src.toUpperCase()) return repl.toUpperCase();
  if (src[0] === src[0].toUpperCase()) return repl[0].toUpperCase() + repl.slice(1);
  return repl;
};

/**
 * Is this section the form's declaration / undertaking?
 *
 * It used to be decided on the section NAME alone (/declaration/i), which works
 * only for a template that happens to call the section "Declaration". A form
 * that numbers its sections instead — "Part 4", with the word Declaration only
 * on the PAGE — was not recognised, so its undertaking text was never built and
 * never printed: the preview showed a bare Place / Date / Signature and the
 * statements the applicant actually agreed to were missing from the record.
 *
 * So ask what the section IS, not what it is called: an explicit sectionType, a
 * name that says so, or — the reliable one — the presence of declaration copy.
 *
 * Deliberately NOT decided by the page title: a section with no declaration
 * copy has no undertaking to show, and routing it here would replace its own
 * field labels ("Signature of the applicant (depositor)") with generic ones.
 *
 * @param {object} sec  a template section OR a built filledPages section
 */
export function isDeclarationSection(sec) {
  if (!sec || typeof sec !== 'object') return false;
  if (sec.sectionType === 'declaration') return true;
  const has = v => Array.isArray(v) ? v.filter(Boolean).length > 0 : !!(typeof v === 'string' && v.trim());
  if (has(sec.declarationText) || has(sec.agreementText)) return true;
  if (Array.isArray(sec.declBlocks) && sec.declBlocks.length > 0) return true;
  return /declaration|undertaking/i.test(String(sec.sectionName || ''));
}

export function toUndertaking(text) {
  if (text === null || text === undefined) return text;
  if (Array.isArray(text)) return text.map(toUndertaking);
  let out = String(text);
  for (const [re, repl] of WORD_MAP) out = out.replace(re, (m) => matchCase(m, repl));
  return out;
}

export default toUndertaking;
